import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../api/client'
import { Badge, Loading } from '../../components/ui'
import { getCategoryLabel, getPlatformLabel } from '../../utils/productLabels'

interface AdminProduct {
  id: string
  title: string
  description: string
  price: number
  status: string
  category: string
  platform: string
  totalSales: number
  totalRevenue: number
  createdAt: string
  seller: {
    id: string
    name: string
    email: string
  }
  _count: {
    reviews: number
    purchases: number
  }
}

interface ProductsResponse {
  products: AdminProduct[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function AdminProductsPage() {
  const [products, setProducts] = useState<AdminProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null)
  const [editForm, setEditForm] = useState({ title: '', price: 0, status: '' })
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    fetchProducts()
  }, [search, statusFilter, page])

  const fetchProducts = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      params.append('page', String(page))
      params.append('limit', '20')

      const data = await api.get<ProductsResponse>(`/admin/products?${params}`)
      setProducts(data.products)
      setTotalPages(data.totalPages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products')
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (product: AdminProduct) => {
    setEditingProduct(product)
    setEditForm({
      title: product.title,
      price: product.price,
      status: product.status,
    })
  }

  const handleSaveEdit = async () => {
    if (!editingProduct) return

    if (!editForm.title.trim() || editForm.title.length < 5) {
      setError('Title must be at least 5 characters')
      return
    }
    if (editForm.price < 1) {
      setError('Price must be at least 1 USDC')
      return
    }

    setActionLoading(true)
    setError('')
    try {
      await api.patch(`/admin/products/${editingProduct.id}`, editForm)
      setEditingProduct(null)
      await fetchProducts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product')
    } finally {
      setActionLoading(false)
    }
  }

  const handleForceApprove = async (productId: string) => {
    if (!confirm('Force approve this product? This will bypass the review process.')) {
      return
    }
    setActionLoading(true)
    setError('')
    try {
      await api.post(`/admin/products/${productId}/force-approve`, {})
      await fetchProducts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve product')
    } finally {
      setActionLoading(false)
    }
  }

  const handleForceReject = async (productId: string) => {
    const reason = prompt('Enter rejection reason:')
    if (!reason) return
    setActionLoading(true)
    setError('')
    try {
      await api.post(`/admin/products/${productId}/force-reject`, { reason })
      await fetchProducts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject product')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (productId: string, productTitle: string) => {
    if (!confirm(`Are you sure you want to delete product "${productTitle}"? This action cannot be undone.`)) {
      return
    }
    setActionLoading(true)
    setError('')
    try {
      await api.delete(`/admin/products/${productId}`)
      await fetchProducts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product')
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusVariant = (status: string): 'green' | 'blue' | 'gold' | 'gray' | 'red' => {
    switch (status) {
      case 'APPROVED': return 'green'
      case 'PENDING_REVIEW': return 'blue'
      case 'DRAFT': return 'gray'
      case 'REJECTED': return 'red'
      case 'DELISTED': return 'red'
      default: return 'gray'
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/admin" className="text-secondary" style={{ fontSize: '14px' }}>Admin</Link>
          <h1>Product Management</h1>
        </div>
      </div>

      {/* Filters */}
      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="input"
            placeholder="Search by title..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select
            className="input select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            style={{ width: 180 }}
          >
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="DELISTED">Delisted</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {isLoading ? (
        <Loading message="Loading products..." />
      ) : products.length === 0 ? (
        <div className="empty-state">
          <h3>No products found</h3>
        </div>
      ) : (
        <>
          <div className="card-static" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Product</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Seller</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Sales</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Created</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{product.title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {getCategoryLabel(product.category)} • {getPlatformLabel(product.platform)}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontSize: '13px' }}>{product.seller.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{product.seller.email}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <Badge variant={getStatusVariant(product.status)}>{product.status.replace('_', ' ')}</Badge>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                      {product.price} pts
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      <div>{product.totalSales} sales</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{product.totalRevenue} pts</div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '13px' }}>
                      {new Date(product.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Link to={`/products/${product.id}`} className="btn btn-sm">View</Link>
                        <button className="btn btn-sm" onClick={() => handleEdit(product)} disabled={actionLoading}>
                          Edit
                        </button>
                        {(product.status === 'PENDING_REVIEW' || product.status === 'DRAFT') && (
                          <button
                            className="btn btn-sm"
                            style={{ color: 'var(--success)' }}
                            onClick={() => handleForceApprove(product.id)}
                            disabled={actionLoading}
                          >
                            Approve
                          </button>
                        )}
                        {(product.status === 'PENDING_REVIEW' || product.status === 'APPROVED') && (
                          <button
                            className="btn btn-sm"
                            style={{ color: 'var(--warning)' }}
                            onClick={() => handleForceReject(product.id)}
                            disabled={actionLoading}
                          >
                            Reject
                          </button>
                        )}
                        <button
                          className="btn btn-sm"
                          style={{ color: 'var(--error)' }}
                          onClick={() => handleDelete(product.id, product.title)}
                          disabled={actionLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem' }}>
              <button className="pagination-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>
                &lt;
              </button>
              <span style={{ padding: '0 1rem' }}>Page {page} of {totalPages}</span>
              <button className="pagination-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                &gt;
              </button>
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card-static" style={{ width: '100%', maxWidth: 400 }}>
            <h3 style={{ marginBottom: '1rem' }}>Edit Product: {editingProduct.title}</h3>
            <div className="form-group">
              <label className="label">Title</label>
              <input
                type="text"
                className="input"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Price (USDC)</label>
              <input
                type="number"
                className="input"
                value={editForm.price}
                onChange={(e) => setEditForm({ ...editForm, price: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div className="form-group">
              <label className="label">Status</label>
              <select
                className="input select"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="DRAFT">Draft</option>
                <option value="PENDING_REVIEW">Pending Review</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="DELISTED">Delisted</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setEditingProduct(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
